import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { pixelRatio2d } from '@/editor/math/const';
import HighlightedLine from './highlightedLine';
import { globalStores } from '@/editor/stores';
import { disposeAllChildrenThenRemoveFromParent } from '../utils';
import { ENTITY_RENDER_ORDER, ENTITY_PRIORITY, wall2dColor, hoverOutlineColor } from '@/editor/constant/entity';
import { EContainerType } from '@/editor/types/entity';
import { EEntityType } from '@/editor/types';
import BaseModel2d from './BaseModel2d';
import { IShapePoint2d } from '@/editor/types/shape';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { sampleOutlines } from '@/editor/utils/wallShape';

class WallModel extends BaseModel2d {
  private _wallMesh: THREE.Mesh;
  private _outlines: IPoint2d[][];
  private _shapeLines: HighlightedLine[];

  constructor(wallId: string, outline: IShapePoint2d[][]) {
    super();

    const sampledOutlines = sampleOutlines(outline);
    const mergedGeo = this.createWallEntGeometry(sampledOutlines);
    const material = new THREE.MeshBasicMaterial({ color: wall2dColor });
    this._wallMesh = new THREE.Mesh(mergedGeo, material);
    // this._wallMesh.renderOrder = ENTITY_RENDER_ORDER.wall;

    this._modelGroup.add(this._wallMesh);
    this._modelGroup.userData.type = EEntityType.Wall;
    this._modelGroup.userData.entityId = wallId;
    this._modelGroup.userData.priority = ENTITY_PRIORITY.wall;
    this._modelGroup.userData.containerType = EContainerType.entity;

    this._shapeLines = [];
    this._outlines = sampledOutlines;

    this.setDisplayOrder(ENTITY_RENDER_ORDER.wall);
    this._wallMesh.updateWorldMatrix(true, true);

    globalStores.editorStateStore.sceneInfo.scene2d.intersectableGroup.add(this._modelGroup);
  }

  private createWallEntGeometry(outlines: IPoint2d[][]) {
    if (!outlines.length) return;

    const geometries: THREE.ShapeGeometry[] = [];
    for (const path of outlines) {
      const len = path.length;
      if (!len) continue;

      const shape = new THREE.Shape();

      shape.moveTo(path[0].x / pixelRatio2d, path[0].y / pixelRatio2d);
      for (let i = 1; i < len; i++) {
        shape.lineTo(path[i].x / pixelRatio2d, path[i].y / pixelRatio2d);
      }
      shape.lineTo(path[0].x / pixelRatio2d, path[0].y / pixelRatio2d);

      const geo = new THREE.ShapeGeometry(shape);
      geometries.push(geo);
    }

    // 合并成一个 BufferGeometry
    const mergedGeo = geometries.length > 1 ? mergeGeometries(geometries) : geometries[0];

    return mergedGeo;
  }

  // 尽量通过模型管理器调用更新
  updateWallModel(wallId: string, outline: IShapePoint2d[][]) {
    const sampledOutlines = sampleOutlines(outline);
    this._outlines = sampledOutlines;
    if (!sampledOutlines.length) return;

    // 替换几何体（先释放旧的）
    const geometry = this.createWallEntGeometry(sampledOutlines);
    if (geometry) {
      this._wallMesh.geometry.dispose();
      this._wallMesh.geometry = geometry;
    }
  }

  createWallHighline() {
    this._outlines.forEach((outline) => {
      for (let i = 0; i < outline.length; i++) {
        const start = outline[i];
        const end = outline[(i + 1) % outline.length];
        const highlightedLine = new HighlightedLine([start, end], ENTITY_RENDER_ORDER.wallOutline);
        highlightedLine.setHighlightedColor(hoverOutlineColor, 2);
        highlightedLine.highlightedLine.renderOrder = ENTITY_RENDER_ORDER.renderOrderTemp;

        this._shapeLines.push(highlightedLine);
        highlightedLine.highlightedLine.position.setZ(ENTITY_RENDER_ORDER.wallOutline + 100);
        this._modelGroup.add(highlightedLine.highlightedLine);
      }
    });
  }

  setWallEntityColor(color: number) {
    if (this._wallMesh.material instanceof THREE.MeshBasicMaterial) this._wallMesh.material.color.set(color);
  }

  setWallHighlightedStatus(color: number) {
    const isHover = color === hoverOutlineColor ? true : false;

    for (const shapeLine of this._shapeLines) {
      this._modelGroup.remove(shapeLine.highlightedLine);
      shapeLine.dispose();
    }

    if (isHover) {
      this.createWallHighline();
    }
  }

  updateVisibleStatus(visible: boolean) {
    this._modelGroup.visible = visible;
  }

  override dispose() {
    disposeAllChildrenThenRemoveFromParent(this._modelGroup);
  }
}

export default WallModel;
